import React, { useState, useEffect } from 'react';
import './CSS/Dashboard.css';
import ManageUsers from './ManageUsers';
import ManageEvents from './ManageEvents';
import ManageProducts from './ManageProducts';
import ManageCategories from './ManageCategories';
import ManageAuditTrails from './ManageAuditTrails';
import ManageContentUpload from './ManageContentUpload';
import ManagePermissions from './ManagePermissions';
import ManagePurchases from './ManagePurchases';
import UserData from './UserData';
import ManageDashboardSettings from './ManageDashboardSettings';
import ManageLauncherSettings from './ManageLauncherSettings';
import ManagePaymentSettings from './ManagePaymentSettings';
import ManageEventWizard from './ManageEventWizard';
import ManageLandingPages from './ManageLandingPages';
import ManageRoles from './ManageRoles';
import ManageGroups from './ManageGroups';
import useAuth from '../hooks/useAuth';
import { hasPermission } from '../utils/permissions';

// The Dashboard is organised in sections that group related tabs. The sidebar
// lists every tab flat, so any panel is one click away; the sections survive as
// the grouping behind the subtab row, which offers the siblings of whatever you
// are looking at.
//
// Trade-off to keep in mind: a flat sidebar is a long list, so on a phone the
// drawer scrolls. That is deliberate - it was chosen over the shorter grouped
// sidebar, which needed two taps to reach anything.
//
// Each tab maps to a granular permission. Admins (legacy role or the "*"
// wildcard) pass every check; other users only see what they're granted. A
// section is hidden entirely when none of its tabs are permitted.
const SECTIONS = [
  {
    key: 'account',
    label: 'User Data',
    // No perm: every authenticated user can see their own data.
    tabs: [{ key: 'profile', label: 'Profile' }],
  },
  {
    key: 'access',
    label: 'Access',
    tabs: [
      { key: 'manageUsers', label: 'Users', perm: 'users:manage' },
      { key: 'manageRoles', label: 'Roles', perm: 'roles:manage' },
      { key: 'manageGroups', label: 'Groups', perm: 'groups:manage' },
      { key: 'managePermissions', label: 'Content Access', perm: 'permissions:manage' },
    ],
  },
  {
    key: 'catalog',
    label: 'Catalog',
    tabs: [
      { key: 'manageProducts', label: 'Products', perm: 'products:manage' },
      { key: 'manageCategories', label: 'Categories', perm: 'categories:manage' },
      { key: 'manageEvents', label: 'Events', perm: 'events:manage' },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    tabs: [
      { key: 'managePurchases', label: 'Purchases', perm: 'purchases:manage' },
      { key: 'managePaymentSettings', label: 'Payment Settings', perm: 'paymentsettings:manage' },
    ],
  },
  {
    key: 'site',
    label: 'Site',
    tabs: [
      { key: 'manageDashboardSettings', label: 'Home Layout', perm: 'dashboardsettings:manage' },
      { key: 'manageLandingPages', label: 'Landing Pages', perm: 'landingpages:manage' },
      { key: 'manageLauncherSettings', label: 'VR Launcher', perm: 'launchersettings:manage' },
      { key: 'manageContentUpload', label: 'Upload Files', perm: 'uploads:manage' },
    ],
  },
  {
    key: 'audit',
    label: 'Audit',
    tabs: [{ key: 'manageAuditTrails', label: 'Audit Trails', perm: 'audit:read' }],
  },
  {
    // The old "Event Wizard" panel, promoted to its own section: it is the live
    // production console (instances, MPD manifests, FFmpeg commands) rather than
    // another catalog CRUD screen. Single tab, so the subtab row stays hidden.
    // Kept last in the sidebar on purpose.
    key: 'broadcast',
    label: 'Broadcast Control',
    tabs: [
      { key: 'manageEventWizard', label: 'Broadcast Control', perm: 'eventwizard:manage' },
    ],
  },
];

const Dashboard = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [activeSection, setActiveSection] = useState('account');
  const [activeTab, setActiveTab] = useState('profile');
  const [refreshPermissions, setRefreshPermissions] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const [tabResetKeys, setTabResetKeys] = useState({
    profile: 0,
    manageUsers: 0,
    manageEvents: 0,
    manageProducts: 0,
    manageCategories: 0,
    manageAuditTrails: 0,
    manageContentUpload: 0,
    managePermissions: 0,
    managePurchases: 0,
    manageDashboardSettings: 0,
    manageLauncherSettings: 0,
    managePaymentSettings: 0,
    manageEventWizard: 0,
    manageLandingPages: 0,
    manageRoles: 0,
    manageGroups: 0,
  });

  const { userData, authLoading } = useAuth();

  // Fetch the user's effective permissions to drive which tabs are visible.
  useEffect(() => {
    const fetchPermissions = async () => {
      const token = localStorage.getItem('auth-token');
      if (!token) return;
      try {
        const response = await fetch(`${backendUrl}/roles/my-permissions`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'auth-token': token },
        });
        const result = await response.json();
        if (result.success) {
          setPermissions(result.permissions || []);
        }
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };
    fetchPermissions();
  }, [backendUrl]);

  const can = (perm) => hasPermission(permissions, perm);

  // Selecting the tab that is already open remounts it (tabResetKeys), which is
  // how the panels reload their data.
  const handleTabChange = (tab) => {
    if (tab === activeTab) {
      setTabResetKeys((prevKeys) => ({
        ...prevKeys,
        [tab]: prevKeys[tab] + 1,
      }));
    } else {
      if (tab === 'managePermissions') {
        setRefreshPermissions((prev) => !prev);
      }
      setActiveTab(tab);
    }
  };

  // The sidebar lists every tab, so picking one has to move the section too:
  // that is what keeps the subtab row showing the right siblings.
  const handleSidebarChange = (tabKey, sectionKey) => {
    setActiveSection(sectionKey);
    handleTabChange(tabKey);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (authLoading) return <div className="loading"></div>;
  if (!userData) return null;

  // Drop tabs the user can't reach, then drop sections left with no tabs.
  const visibleSections = SECTIONS
    .map((section) => ({
      ...section,
      tabs: section.tabs.filter((tab) => !tab.perm || can(tab.perm)),
    }))
    .filter((section) => section.tabs.length > 0);

  const currentSection =
    visibleSections.find((section) => section.key === activeSection) || visibleSections[0];

  // Every permitted tab as one flat list, each carrying the section it belongs
  // to. The sidebar shows all of them at once; SECTIONS still groups them so the
  // subtab row can offer the siblings once you are inside one.
  const sidebarEntries = visibleSections.flatMap((section) =>
    section.tabs.map((tab) => ({ ...tab, sectionKey: section.key }))
  );

  return (
    <div className="Dashboard dark-mode">
      {/* Mobile Menu Toggle Button */}
      <button
        className="mobile-menu-toggle"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      {/* Overlay backdrop for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <ul>
          {sidebarEntries.map((entry) => (
            <li
              key={entry.key}
              className={activeTab === entry.key ? 'active' : ''}
              onClick={() => handleSidebarChange(entry.key, entry.sectionKey)}
            >
              {entry.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="dashboard-content">
        {/* Sub-navigation for the current section. Hidden when the section has
            a single tab, since the sidebar entry already says what it is.

            The extra "-nav" class marks this strip as a duplicate of the
            sidebar, which is why it is the one hidden on mobile. Panels that
            borrow the same styling for their own internal tabs (UserData's
            Profile / My Content / My Orders) must keep theirs everywhere. */}
        {currentSection && currentSection.tabs.length > 1 && (
          <div className="dashboard-subtabs dashboard-subtabs-nav">
            {currentSection.tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`dashboard-subtab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'profile' && <UserData userData={userData} setUserData={() => {}} backendUrl={backendUrl} resetKey={tabResetKeys.profile} />}
        {activeTab === 'manageUsers' && can('users:manage') && <ManageUsers backendUrl={backendUrl} resetKey={tabResetKeys.manageUsers} />}
        {activeTab === 'manageEvents' && can('events:manage') && <ManageEvents backendUrl={backendUrl} resetKey={tabResetKeys.manageEvents} />}
        {activeTab === 'manageProducts' && can('products:manage') && <ManageProducts backendUrl={backendUrl} resetKey={tabResetKeys.manageProducts} />}
        {activeTab === 'manageCategories' && can('categories:manage') && <ManageCategories backendUrl={backendUrl} resetKey={tabResetKeys.manageCategories} />}
        {activeTab === 'managePermissions' && can('permissions:manage') && <ManagePermissions backendUrl={backendUrl} resetKey={tabResetKeys.managePermissions} refreshPermissions={refreshPermissions} />}
        {activeTab === 'managePurchases' && can('purchases:manage') && <ManagePurchases backendUrl={backendUrl} resetKey={tabResetKeys.managePurchases} />}
        {activeTab === 'manageAuditTrails' && can('audit:read') && <ManageAuditTrails backendUrl={backendUrl} resetKey={tabResetKeys.manageAuditTrails} />}
        {activeTab === 'manageContentUpload' && can('uploads:manage') && <ManageContentUpload backendUrl={backendUrl} resetKey={tabResetKeys.manageContentUpload} />}
        {activeTab === 'manageDashboardSettings' && can('dashboardsettings:manage') && <ManageDashboardSettings backendUrl={backendUrl} resetKey={tabResetKeys.manageDashboardSettings} />}
        {activeTab === 'manageLauncherSettings' && can('launchersettings:manage') && <ManageLauncherSettings backendUrl={backendUrl} resetKey={tabResetKeys.manageLauncherSettings} />}
        {activeTab === 'managePaymentSettings' && can('paymentsettings:manage') && <ManagePaymentSettings backendUrl={backendUrl} resetKey={tabResetKeys.managePaymentSettings} />}
        {activeTab === 'manageEventWizard' && can('eventwizard:manage') && <ManageEventWizard backendUrl={backendUrl} resetKey={tabResetKeys.manageEventWizard} />}
        {activeTab === 'manageLandingPages' && can('landingpages:manage') && <ManageLandingPages backendUrl={backendUrl} resetKey={tabResetKeys.manageLandingPages} />}
        {activeTab === 'manageRoles' && can('roles:manage') && <ManageRoles backendUrl={backendUrl} resetKey={tabResetKeys.manageRoles} />}
        {activeTab === 'manageGroups' && can('groups:manage') && <ManageGroups backendUrl={backendUrl} resetKey={tabResetKeys.manageGroups} />}

        <button className="logout-button" onClick={() => {
          localStorage.removeItem('auth-token');
          window.location.href = '/';
        }}>Logout</button>
      </div>
    </div>
  );
};

export default Dashboard;
